import * as React from 'react';
import { AnyObject } from 'final-form';
import LeaseClause from 'src/models/LeaseClause';
import { Page, Text, Document, StyleSheet, View } from '@react-pdf/renderer';
import PropertyUtil from 'src/util/PropertyUtil';
import moment from 'moment';
import MathUtil from 'src/util/MathUtil';
// import SignatureBlockManager from './SignatureBlockManager';
import Acknowledgement from 'src/models/leasing/Acknowledgement';
import SignatureBlock from 'src/models/leasing/SignatureBlock';
import LeaseClauseType from 'src/models/LeaseClauseType';
import LocationModel from 'src/models/leasing/LocationModel';
import ContractAddendumClause from '../redesign/ContractGenerationModels/ContractAddendumClause';
import Lease from 'src/models/leasehold/Lease';

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#FFFFFF',
        paddingTop: '1.5in',
        paddingBottom: '1in',
        paddingHorizontal: '0.5in',
        fontFamily: 'Times-Roman',
    },
    pageNumbers: {
        position: 'absolute',
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 10,
        color: '#8e9094',
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
    },
    bold: {
        fontFamily: 'Times-Bold',
    },
    fontNormal: {
        fontFamily: 'Times-Roman',
        fontSize: '8pt',
        lineHeight: 1.25,
    },
    font9pt: {
        fontFamily: 'Times-Roman',
        fontSize: '9pt',
        lineHeight: 1.25,
    },
    noticeHeadingSize: {
        fontSize: '12pt',
    },
    mt1: {
        marginTop: 10,
    },
    mt2: {
        marginTop: 20,
    },
    mt3: {
        marginTop: 30,
    },
    mt4: {
        marginTop: 40,
    },
    center: {
        textAlign: 'center',
    },
    text: {
        textAlign: 'justify',
    },
    sbTitle: {
        textAlign: 'center',
        marginBottom: 10,
    },
    underlined: {
        textDecoration: 'underline',
    },
    mailingInfo: {
        fontSize: 10,
        fontFamily: 'Times-Roman',
    },
    acknowledgementHeading: {
        textAlign: 'center',
        fontSize: '10pt',
        fontWeight: 'bold',
    },
    exhibitHeading: {
        textAlign: 'center',
        fontSize: '12pt',
        fontWeight: 'bold',
    },
    leaseHeading: {
        fontSize: 20,
        textAlign: 'center',
        fontFamily: 'Times-Bold',
    },
    doubleLines: {
        borderTop: '1px solid black',
        borderBottom: '1px solid black',
    },
    h2: {
        fontSize: 14,
    },
    firstLvl: {
        marginLeft: 20,
    },
    secondLvl: {
        marginLeft: 54,
    },
    thirdLvl: {
        marginLeft: 70,
    },
    fourthLvl: {
        marginLeft: 86,
    },
});

export interface IAethonLeaseStateProps {
    contractData: Lease;
    leaseClauses?: ContractAddendumClause[];
    leaseClauseTypes?: LeaseClauseType[];
    location?: LocationModel;
}

export default class AethonLease extends React.Component<IAethonLeaseStateProps> {
    private additionalProvisionNumber = 0;

    displayOptionToExtend = () => {
        if (this.props?.contractData?.leasehold?.renewalOption === true) {
            this.additionalProvisionNumber++;
            return (
                <Text style={[{ fontSize: '10pt' }, styles.mt2, styles.text]}>
                    {this.additionalProvisionNumber}
                    {') '}For the same consideration recited in this lease, Lessor hereby grants Lessee the option to extend the primary term of this
                    lease for an additional
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionTerm + '', 10), ' '),
                            'OPTION TERM WORD'
                        )}{' '}
                        ({PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM')}){' '}
                        {this.props.contractData?.leasehold?.optionTermUnits}
                    </Text>
                    {' '}from the expiration of the original primary term hereof as to all or any
                    portion of the acreage then held hereunder which would expire unless so extended. This option may be exercised by Lessee, or its
                    successors and assigns, at any time during the primary term hereof by tendering to Lessor the sum of{' '}
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionBonusAmount + '', 10), ' '),
                            'OPTION AMOUNT'
                        )}{' '}dollars{' '}
                        (${parseInt(this.props.contractData?.leasehold?.optionBonusAmount + '', 10)})
                    </Text>
                    {' '}per net mineral acre for each acre so extended, which
                    payment shall cover the
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionTerm + '', 10), ' '),
                            'OPTION TERM WORD'
                        )}{' '}
                        ({PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM')}){' '}
                        {this.props.contractData?.leasehold?.optionTermUnits}
                    </Text>
                    {' '}of the extended term and Lessor acknowledges that there will be no rental
                    payments due for or during the extended term. Should the Primary Term be extended as herein provided, it shall be considered that
                    this lease originally provided for a Primary Term of
                    {this.displayTotalPrimaryTerm()}
                    . Should this option be exercised as herein provided, it is agreed that Lessee
                    may execute and file of record an appropriate recordable instrument evidencing the exercise of this option.
                </Text>
            );
        } else {
            return <></>;
        }
    };

    additionalProvisions = () => {
        let renderClauses: ContractAddendumClause[] = [];
        this.props.contractData?.contractClauses?.forEach((llc: AnyObject, index: number) => {
            let clause: ContractAddendumClause = !!this.props?.leaseClauses
                ? this.props.leaseClauses?.find((lc) => lc.id === llc.contractAddendumClauseId)
                : undefined;

            if (!!clause) {
                renderClauses.push(clause);
            }
        });

        return renderClauses?.map((rc: LeaseClause, index: number) => {
            this.additionalProvisionNumber++;
            return (
                <Text key={index} style={[{ fontSize: '10pt' }, styles.mt2, styles.text]}>
                    {this.additionalProvisionNumber}) {`${rc.clauseText}`}
                </Text>
            );
        });
    };

    adhocProvisions = () => {
        const adHocClauses = this?.props?.contractData?.adHocClauses;
        if (adHocClauses?.length > 0) {
            return this.props?.contractData?.adHocClauses?.map((lc: LeaseClause, index: number) => {
                let type: string = !!this.props?.leaseClauseTypes
                    ? this.props?.leaseClauseTypes?.find((lct) => lct.id === lc.leaseClauseTypeId)?.name
                    : '';

                this.additionalProvisionNumber++;
                return (
                    <Text key={index} style={[{ fontSize: '10pt' }, styles.mt2, styles.text]}>
                        {this.additionalProvisionNumber}) {`${type}: ${lc.clauseText}`}
                    </Text>
                );
            });
        }
        return <div />;
    };

    displaySignatureBlocks = () => {
        return this.props?.contractData
            ? this.props?.contractData?.acknowledgements?.map((la: Acknowledgement) => {
                return (
                    la.signatureBlocks.length > 0 &&
                    la.signatureBlocks.map((sb: SignatureBlock, index: number) => {
                        return (
                            <View key={index} wrap={false} style={{ marginRight: 5 }}>
                                {!!sb.title && sb?.title.length > 0 && (
                                    <Text style={[{ fontSize: '10pt' }, styles.mt3, styles.text, styles.bold]}>{sb?.title}</Text>
                                )}
                                {/* <Text style={[{ fontSize: '10pt' }, styles.mt2, styles.text]}>Lessor:</Text> */}
                                <Text style={[{ fontSize: '10pt' }, styles.mt4, styles.text, styles.bold]}>
                                    ____________________________________
                                </Text>
                                <Text style={[{ fontSize: '10pt' }, styles.text, styles.bold]}>{sb.ownerName}</Text>
                            </View>
                        );
                    })
                );
            })
            : [];
    };

    displayAcknowledgements = () => {
        return this.props?.contractData
            ? this.props?.contractData?.acknowledgements?.map((la: Acknowledgement, index: number) => {
                return (
                    <View key={index} wrap={false}>
                        <Text style={[{ fontSize: '10pt' }, styles.mt3, styles.text]}>{la.text}</Text>
                    </View>
                );
            })
            : [];
    };

    termValDisplay = (mode: string): string => {
        if (mode === 'normal') {
            let primaryTerm: number = !!this.props?.contractData ? parseInt(this.props?.contractData?.leasehold?.primaryTerm + '', 10) : undefined;
            let primaryTermUnits: string = !!this.props?.contractData ? this.props?.contractData?.leasehold?.primaryTermUnits : undefined;

            if (!primaryTerm && !primaryTermUnits) {
                return 'Zero (0) Years';
            }

            let primaryTermString = MathUtil.numberToEnglish(primaryTerm, ' ');

            return `${primaryTermString.charAt(2).toUpperCase() + primaryTermString.slice(3)} (${primaryTerm}) ${primaryTermUnits.charAt(0).toUpperCase() + primaryTermUnits.slice(1)
                }`;
        }

        if (mode === 'option') {
            let optionTerm: number = !!this.props?.contractData ? parseInt(this.props?.contractData?.leasehold?.optionTerm + '', 10) : undefined;
            let optionTermUnits: string = !!this.props?.contractData ? this.props?.contractData?.leasehold?.optionTermUnits : undefined;
            if (!optionTerm && !optionTermUnits) {
                return 'Zero (0) Years';
            }

            let optionTermString = MathUtil.numberToEnglish(optionTerm, ' ');

            return `${optionTermString.charAt(2).toUpperCase() + optionTermString.slice(3)} (${optionTerm}) ${optionTermUnits.charAt(0).toUpperCase() + optionTermUnits.slice(1)
                }`;
        }

        return '';
    };

    displayTotalPrimaryTerm = () => {
        let primeTerm = PropertyUtil.SafeGet(() => this.props.contractData.leasehold?.primaryTerm, 'PRIMARY TERM');
        let primeUnit = this.props.contractData?.leasehold?.primaryTermUnits;

        let optionTerm = PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM');
        let optionUnit = this.props.contractData?.leasehold?.optionTermUnits;

        if (!isNaN(primeTerm) && !isNaN(optionTerm)) {
            if (optionUnit !== primeUnit) {
                return (
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(primeTerm, 10), ' '),
                            'PRIMARY TERM WORD'
                        )}{' '}
                        ({primeTerm}){' '}
                        {this.props.contractData?.leasehold?.primaryTermUnits}
                        and
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionTerm + '', 10), ' '),
                            'OPTION TERM WORD'
                        )}{' '}
                        ({PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM')}){' '}
                        {this.props.contractData?.leasehold?.optionTermUnits}
                    </Text>
                );
            }

            primeTerm += optionTerm;
        }

        return (
            <Text style={[styles.bold, { fontSize: '9pt' }]}>
                {PropertyUtil.SafeGet(
                    () => MathUtil.numberToEnglish(parseInt(primeTerm, 10), ' '),
                    'PRIMARY TERM WORD'
                )}{' '}
                ({primeTerm}){' '}
                {this.props.contractData?.leasehold?.primaryTermUnits}
            </Text>
        );
    };

    displayRoyaltyFraction = () => {
        let royaltyFraction = PropertyUtil.SafeGet(() => MathUtil.decimalToFraction(this.props.contractData?.leasehold?.royalty), 'Royalty');
        let splitRoyaltyFraction = royaltyFraction.split('/');

        let numerator = splitRoyaltyFraction[0];
        let denominator = splitRoyaltyFraction[1];

        let royaltyToEnglish = MathUtil.fractionToEnglish(numerator, denominator);
        console.log(`Fraction To Words (${this.props.contractData?.leasehold?.royalty})  => `, royaltyToEnglish);

        // Set variable so you only have to Convert once
        let combinedString = royaltyToEnglish + ' (' + royaltyFraction + ')';
        return combinedString;
    };

    // signatureBlocks = (leaseValues: AnyObject) => {
    //     return <SignatureBlockManager leaseValues={leaseValues} styles={styles} />;
    // };

    // tslint:disable: max-line-length
    render() {
        const leaseValues = PropertyUtil.SafeGet(() => this.props.contractData);
        // const firstIncludedTract = (leaseValues?.tracts?.find((tract: AnyObject) => tract.includeTract === true);
        const totalGrossAcres = leaseValues?.tracts?.length > 0 ? (leaseValues?.tracts?.reduce((acc: number, tract: AnyObject) => {
            if (tract.includeTract === true) {
                let tempGrossAcres = parseFloat(tract.grossAcres) || 0;
                return acc + tempGrossAcres;
            } else {
                return acc;
            }
        }, 0)) : ((leaseValues?.tracts?.length < 1 && leaseValues?.leasehold?.provisionalGrossAcres !== undefined) ?
                 this.props.contractData.leasehold.provisionalGrossAcres : 0);

        return (
            <Document>
                <Page size="LEGAL" style={styles.page}>
                    <Text style={[styles.noticeHeadingSize]}>
                        NOTICE OF CONFIDENTIALITY RIGHTS: IF YOU ARE A NATURAL PERSON, YOU MAY REMOVE OR STRIKE ANY OR ALL OF THE FOLLOWING
                        INFORMATION FROM THIS INSTRUMENT BEFORE IT IS FILED FOR RECORD IN THE PUBLIC RECORDS:{' '}
                        <Text style={[styles.bold]}>YOUR SOCIAL SECURITY NUMBER OR YOUR DRIVER’S LICENSE NUMBER.</Text>
                    </Text>

                    <Text style={[{ fontSize: '12pt', textAlign: 'center', fontFamily: 'Times-Bold' }, styles.mt2]}>PAID UP OIL AND GAS LEASE</Text>

                    <Text style={[styles.fontNormal, styles.mt2, styles.text]} wrap={true}>
                        <Text style={{ fontSize: '10pt' }}>THIS LEASE AGREEMENT</Text> is made as of the{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>
                            {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('Do'), 'DAY')}
                        </Text>{' '}
                        day of{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>
                            {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('MMMM'), 'MONTH')},{' '}
                            {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('YYYY'), 'YEAR')}
                        </Text>
                        , by and between{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>
                            {PropertyUtil.SafeGet(() => leaseValues.ownersOnDocument, 'LESSOR NAME')}
                        </Text>
                        , whose address is {PropertyUtil.SafeGet(() => leaseValues.ownersAddressOnDocument, 'LESSOR ADDRESS')}, Party of the first
                        part, hereinafter called "Lessor" (whether one or more), and{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>{PropertyUtil.SafeGet(() => leaseValues.lesseeName, 'LESSEE NAME')}</Text>,
                        whose address is{' '}
                        {PropertyUtil.SafeGet(
                            () =>
                                leaseValues.lesseeAddressLine1 +
                                ', ' +
                                leaseValues.lesseeCity +
                                ', ' +
                                leaseValues.lesseeState +
                                ', ' +
                                leaseValues.lesseeZipCode,
                            'LESSEE ADDRESS'
                        )}
                        , Party of the second part, hereinafter called "Lessee".
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                        1. In consideration of a cash bonus in hand paid and the covenants herein contained, Lessor hereby grants, leases and lets
                        exclusively to Lessee the following described land, hereinafter called leased premises: (use Exhibit "A" for long
                        description):
                    </Text>

                    {/* <Text style={[styles.fontNormal, styles.mt1, styles.text, styles.bold, styles.underlined]}>DESCRIPTION</Text> */}

                    <Text style={[styles.mt1, styles.text, styles.bold, { fontSize: '10pt' }]} wrap={true}>
                        {!leaseValues?.legalDescription
                            ? 'FOR ADDITIONAL PROVISIONS TO THIS LEASE, PLEASE SEE EXHIBIT “A” ATTACHED TO AND MADE A PART OF FOR ALL PURPOSES.'
                            : leaseValues?.legalDescription?.length > 1000
                                ? 'FOR LEGAL DESCRIPTION AND ADDITIONAL PROVISIONS TO THIS LEASE, PLEASE SEE EXHIBIT “A” ATTACHED TO AND MADE A PART OF FOR ALL PURPOSES.'
                                : leaseValues?.legalDescription}
                    </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                        in the county of{' '}
                        <Text style={[styles.font9pt, styles.text, styles.bold]}>
                            {!!this.props.location && this.props?.location?.counties[0]}
                        </Text>
                        , State of {!!this.props.location && this.props?.location?.state}, containing{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>
                            {(PropertyUtil.SafeGet(() => totalGrossAcres, 'GROSS ACRES') as number)?.toFixed(4)}
                        </Text>{' '}
                        gross acres, more or less (including any interests therein which Lessor may hereafter acquire by reversion, prescription or
                        otherwise), for the purpose of exploring for, developing, producing and marketing oil and gas, along with all hydrocarbon and
                        nonhydrocarbon substances produced in association therewith. The term "gas" as used herein includes helium, carbon dioxide and
                        other commercial gases, as well as hydrocarbon gases. All other minerals are reserved to the Lessor herein. In addition to the
                        above described leased premises, this lease also covers accretions and any small strips or parcels of land now or hereafter
                        owned by Lessor which are contiguous or adjacent to the above described leased premises, and, in consideration of the
                        aforementioned cash bonus, Lessor agrees to execute at Lessee's request any additional or supplemental instruments for a more
                        complete or accurate description of the land so covered. For the purpose of determining the amount of any shut in royalties
                        hereunder, the number of gross acres above specified shall be deemed correct, whether actually more or less.
                    </Text>

                    {/* <Text style={[styles.fontNormal, styles.mt2, styles.text]}>
                        and containing ({PropertyUtil.SafeGet(() => totalGrossAcres, 'GROSS ACRES')}) acres more or less (the "Lands" or "Leased
                        Premises").
                    </Text> */}

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                        2. This lease, which is a "paid up" lease requiring no rentals, shall be in force for a primary term of{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>{this.termValDisplay('normal')}</Text> from{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>
                            {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('MMMM DD, YYYY'), 'LEASE DATE')}
                        </Text>{' '}
                        , and for as long thereafter as oil or gas or other substances covered hereby are produced in paying quantities from the
                        leased premises or from lands pooled therewith or this lease is otherwise maintained in effect pursuant to the provisions
                        hereof.
                    </Text>

                    {/* <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                        It is agreed that this lease shall remain in force for a term of{' '}
                        {PropertyUtil.SafeGet(() => MathUtil.numberToEnglish(parseInt(leaseValues?.leasehold?.primaryTerm, 10)), 'LEASE TERM WORD')} (
                        {PropertyUtil.SafeGet(() => leaseValues.leasehold?.primaryTerm, 'LEASE TERM')}) {leaseValues?.leasehold?.primaryTermUnits}{' '}
                        from {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('MMMM-DD-YYYY'), 'LEASE DATE')} ( herein called
                        primary term) and so long thereafter as oil or gas, or either of them, is produced from said land by Lessee.
                    </Text> */}

                    <View>
                        <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                            3. Royalties on oil, gas and other substances produced and saved hereunder shall be paid by Lessee to Lessor as follows:
                        </Text>
                        <Text style={[styles.fontNormal, styles.mt1, styles.text, styles.firstLvl]} wrap={true}>
                            (a){'       '}For oil and other liquid hydrocarbons separated at Lessee's separator facilities, the royalty shall be{' '}
                            <Text style={[styles.bold, { fontSize: '9pt' }]}>
                                {/* ({PropertyUtil.SafeGet(() => MathUtil.decimalToFraction(leaseValues?.leasehold?.royalty), 'Royalty')}) */}
                                {this.displayRoyaltyFraction()}
                            </Text>{' '}
                            of such production, to be delivered at Lessee's option to Lessor at the wellhead or to Lessor's credit at the oil
                            purchaser's transportation facilities, provided that Lessee shall have the continuing right to purchase such production at
                            the wellhead market price then prevailing in the same field (or if there is no such price then prevailing in the same
                            field, then in the nearest field in which there is such a prevailing price) for production of similar grade and gravity;
                        </Text>
                        <Text style={[styles.fontNormal, styles.mt1, styles.text, styles.firstLvl]} wrap={true}>
                            (b){'       '}For gas (including casinghead gas) and all other substances covered hereby, the royalty shall be{' '}
                            <Text style={[styles.bold, { fontSize: '9pt' }]}>
                                {/* ({PropertyUtil.SafeGet(() => MathUtil.decimalToFraction(leaseValues?.leasehold?.royalty), 'Royalty')}) */}
                                {this.displayRoyaltyFraction()}
                            </Text>{' '}
                            of the proceeds realized by Lessee as from the sale thereof, less a proportionate part of ad valorem taxes and production
                            severance, or other excise taxes and the costs incurred by Lessee in delivering, processing or otherwise marketing such
                            gas or other substances, provided that Lessee shall have the continuing right to purchase such production at the
                            prevailing wellhead market price paid for production of similar quality in the same field (or if there is no such price
                            then prevailing in the same field, then in the nearest field in which there is such a prevailing price) pursuant to
                            comparable purchase contracts entered into on the same or nearest preceding date as the date on which Lessee commences its
                            purchases hereunder; and
                        </Text>
                        <Text style={[styles.fontNormal, styles.mt1, styles.text, styles.firstLvl]} wrap={true}>
                            (c){'       '}If at the end of the primary term or any time thereafter one or more wells on the leased premises or lands
                            pooled therewith are capable of producing oil or gas or other substances covered hereby in paying quantities, but such
                            well or wells are either shut in or production therefrom is not being sold by Lessee, such well or wells shall
                            nevertheless be deemed to be producing in paying quantities for the purpose of maintaining this lease. If for a period of
                            90 consecutive days such well or wells are shut in or production therefrom is not being sold by Lessee, then Lessee shall
                            pay shut in royalty of one dollar per acre then covered by this lease, such payment to be made to Lessor or to Lessor's
                            credit in the depository designated below, on or before the end of said 90 day period and thereafter on or before each
                            anniversary of the end of said 90 day period while the well or wells are shut in or production therefrom is not being sold
                            by Lessee; provided that if this lease is otherwise being maintained by operations, or if production is being sold by
                            Lessee from another well or wells on the leased premises or lands pooled therewith, no shut in royalty shall be due until
                            the end of the 90 day period next following cessation of such operations or production. Lessee's failure to properly pay
                            shut in royalty shall render Lessee liable for the amount due, but shall not operate to terminate this lease.{' '}
                        </Text>
                    </View>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                        4. All shut in royalty payments under this lease shall be paid or tendered to Lessor or to Lessor's credit in{' '}
                        <Text style={[styles.fontNormal, styles.bold, { fontSize: '9pt' }]}>
                            {' '}
                            DIRECT TO LESSOR AT ABOVE ADDRESS or its successors
                        </Text>
                        , which shall be Lessor's depository agent for receiving payments regardless of changes in the ownership of said land. All
                        payments or tenders may be made in currency, or by check or by draft and such payments or tenders to Lessor or to the
                        depository by deposit in the U.S. Mails in a stamped envelope addressed to the depository or to the Lessor at the last address
                        known to Lessee shall constitute proper payment. If the depository should liquidate or be succeeded by another institution, or
                        for any reason fail or refuse to accept payment hereunder, Lessor shall, at Lessee's request, deliver to Lessee a proper
                        recordable instrument naming another institution as depository agent to receive payments.{' '}
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                        5. If Lessee drills a well which is incapable of producing in paying quantities (hereinafter called "dry hole") on the leased
                        premises or lands pooled therewith, or if all production (whether or not in paying quantities) permanently ceases from any
                        cause, then in the event this lease is not otherwise being maintained in force it shall nevertheless remain in force if Lessee
                        commences operations for reworking an existing well or for drilling an additional well or for otherwise obtaining or restoring
                        production on the leased premises or lands pooled therewith within 90 days after completion of operations on such dry hole or
                        within 90 days after such cessation of all production. If at the end of the primary term, or at any time thereafter, this
                        lease is not otherwise being maintained in force but Lessee is then engaged in drilling, reworking or any other operations
                        reasonably calculated to obtain or restore production therefrom, this lease shall remain in force so long as any one or more
                        of such operations are prosecuted with no cessation of more than 90 consecutive days, and if any such operations result in the
                        production of oil or gas or other substances covered hereby, as long thereafter as there is production in paying quantities
                        from the leased premises or lands pooled therewith. After completion of a well capable of producing in paying quantities
                        hereunder, Lessee shall drill such additional wells on the leased premises or lands pooled therewith as a reasonably prudent
                        operator would drill under the same or similar circumstances to (a) develop the leased premises as to formations then capable
                        of producing in paying quantities on the leased premises or lands pooled therewith, or (b) to protect the leased premises from
                        uncompensated drainage by any well or wells located on other lands not pooled therewith. There shall be no covenant to drill
                        exploratory wells or any additional wells except as expressly provided herein.
                    </Text>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { width: '100%', flexWrap: 'wrap' }]}>
                            6. (A){'       '}Lessee, at its option, is hereby given the right and power to pool or combine the leased premises, or any
                            portion thereof, as to oil and gas, or either of them, with any other land covered by this lease, or with any other
                            contiguous land, lease or leases to the extent stipulated herein.
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.secondLvl,
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                marginTop: 16,
                            },
                        ]}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { width: '100%' }]}>
                            (i){'       '}Units pooled for oil hereunder may include any amount of acreage up to, but not exceeding:
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.thirdLvl,
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                marginTop: 16,
                            },
                        ]}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { width: '100%' }]}>
                            (a){'       '}80 acres for each oil well that is not a Horizontal Drainhole Well as defined by Railroad Commission of
                            Texas Statewide Rule 86(5) (a “Horizontal Drainhole Well”); or
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.thirdLvl,
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                marginTop: 16,
                            },
                        ]}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(b)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            if the oil well is a Horizontal Drainhole Well, 640 acres plus a maximum acreage tolerance of 10%.
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.secondLvl,
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                marginTop: 16,
                            },
                        ]}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(ii)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            Units pooled for gas hereunder may include any amount of acreage up to, but not exceeding:
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.thirdLvl,
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                marginTop: 16,
                            },
                        ]}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(a)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            640 acres, plus a maximum acreage tolerance of 10%, around each gas well that is not a Horizontal Drainhole Well; or
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.thirdLvl,
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                marginTop: 16,
                            },
                        ]}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(b)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            if the gas well is a Horizontal Drainhole Well, the greater of (x) 640 acres plus a maximum acreage tolerance of 10%, or
                            (y) 640 acres plus an additional amount of acreage equal to 40 acres for each 500 feet of horizontal displacement of the
                            well bore between the first and last Take Points.
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.secondLvl,
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                marginTop: 16,
                            },
                        ]}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(iii)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            In any case, an area larger (but not smaller) than those described in (i) or (ii) above, (x) if permitted by the Texas
                            Railroad Commission in accordance with its rules; or (y) in order to conform to the well spacing or density pattern of the
                            Railroad Commission of Texas under the Statewide or Special Field Rules applicable to the reservoir or field interval in
                            question. The terms “oil well” and “gas well” mean the well classification given to the applicable well by the Railroad
                            Commission of Texas.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(B)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            Lessee under the provisions hereof may pool or combine acreage covered by this lease or any portion thereof as above
                            provided as to oil in any one or more strata and as to gas in any one or more strata. The units formed by pooling as to
                            any stratum or strata need not conform in size or area with the units into which the lease is pooled in any other stratum
                            or strata and oil units need not conform to gas units. The pooling in one or more instances shall not exhaust the rights
                            of the lessee to pool this lease or portions thereof into other units, and pooling for a vertical well does not preclude
                            separate pooling for Horizontal Drainhole Wells. A unit formed containing more than one stratum may be modified at any
                            time to exclude one or more strata, so long as there is no production of unitized substances from such excluded stratum or
                            strata at the time of such modification.
                        </Text>
                    </View>
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(C)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            Lessee shall file for record in the appropriate records of the county in which the leased premises are located an
                            instrument describing and designating the pooled unit and upon such recordation the unit will be effective as to all
                            parties hereto, their heirs successors and assigns irrespective of whether or not the unit is likewise effective as to all
                            other owners of surface, mineral, royalty or other rights in land included in such unit.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(D)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            Lessee may at its discretion exercise its pooling option before or after commencing operations or completing an oil or gas
                            well on the leased premises and the pooled unit may include but it is not required to include land or leases upon which a
                            well capable of producing oil or gas in paying quantities has theretofore been completed or upon which operations for the
                            drilling of a well for oil or gas have theretofore been commenced. A pooled unit established hereunder shall be effective
                            for all purposes even though there may be mineral, royalty or leasehold interests in the lands included in the unit which
                            are not effectively pooled or unitized.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(E)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            If any pooled unit formed hereunder contains less than the maximum number of acres specified above, Lessee may, at any
                            time, whether before or after production is obtained, enlarge the pooled unit by adding additional acreage, but the
                            enlarged unit shall in no event exceed the applicable size set forth above. If an existing unit is enlarged or otherwise
                            amended, the amendment will be effective on the first day of the first month following its recordation in the applicable
                            county, unless the amendment states a different effective date.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(F)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            For purposes of maintaining this lease in force and effect, operations or production anywhere on a pooled unit or units
                            including any part of the leased premises shall be considered to be operations or production on this lease. For the
                            purpose of computing the royalties to which owners of royalties and payments out of production and each of them shall be
                            entitled on production of oil and gas, or either of them, from the pooled unit, there shall be allocated to the land
                            covered by this lease and included in said unit (or to each separate tract within the unit if this lease covers separate
                            tracts within the unit) a pro rata portion of the oil and gas, or either of them, produced from the pooled unit after
                            deducting that used for operations on the pooled unit. There shall be allocated to the acreage covered by this lease and
                            included in the pooled unit (or to each separate tract within the unit if this lease covers separate tracts within the
                            unit) that pro rata portion of the oil and gas, or either of them, produced from the pooled unit which the number of
                            surface acres covered by this lease (or in each such separate tract) and included in the pooled unit bears to the total
                            number of surface acres included in the pooled unit.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(G)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            By filing of record in the applicable county a declaration of termination, Lessee may terminate any pooled unit created
                            hereunder at any time, so long as at such time there is no production in paying quantities from such pooled unit.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(H)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            If this lease now or hereafter covers separate tracts, no pooling of royalty interest as between any separate tracts is
                            intended or shall be implied merely from the inclusion of such separate tracts within this lease but Lessee shall
                            nevertheless have the right to pool as provided herein with consequent allocation of production as provided above. The
                            term “separate tract” means any tract with royalty ownership differing, now or hereafter, either as to parties or amounts,
                            from that of any other part of the leased premises. Pooling hereunder shall not constitute a cross-conveyance of
                            interests.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={false}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 18 }]}>(I)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            In addition to the creation of pooled units as provided for in this provision, Lessee may, at its option, drill Cross Unit
                            Wells.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={false}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 18 }]}>(J)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%', flexWrap: 'wrap' }]}>
                            For the purpose of computing the royalty to which owners of royalties and payments out of production are entitled on
                            production of oil or gas from a Cross Unit Well, there shall be allocated to each pooled unit containing Take Points
                            in a Cross Unit Well a pro rata portion of the production from the Cross Unit Well, which pro rata portion is equal to
                            the total production from the Cross Unit Well multiplied by a fraction, the numerator of which is the Productive Drainhole
                            Length in the applicable pooled unit and the denominator of which is the total Productive Drainhole Length in the Cross Unit Well.
                            The portion of the production from a Cross Unit Well allocated to a pooled unit containing this lease shall be further allocated
                            to the owners of royalties and payments out of production under this lease as provided for in paragraph F hereof.
                            Operations on or production from a Cross Unit Well shall be deemed to be operations on or production on this lease.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 14,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(K)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 14, width: '100%', flexWrap: 'wrap' }]}>
                            Certain Defined Terms:
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 60,
                        }}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(i)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            “Cross Unit Well” means a Horizontal Drainhole Well with Take Points in: (x) two or more pooled units, or (y) a pooled
                            unit and one or more leases not included in such pooled unit.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 60,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(ii)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            “Take Point” means any point along a horizontal wellbore where oil or gas can be produced in the wellbore from the
                            reservoir or field interval in question.
                        </Text>
                    </View>

                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            marginTop: 16,
                            marginLeft: 60,
                        }}
                        wrap={true}
                    >
                        <Text style={[styles.fontNormal, styles.text, { marginRight: 16 }]}>(iii)</Text>
                        <Text style={[styles.fontNormal, styles.text, { marginLeft: 16, width: '100%' }]}>
                            “Productive Drainhole Length” means the horizontal length of the wellbore path that begins at the first Take Point and
                            runs along the actually surveyed path of the wellbore to the last Take Point. This length will be determined by an “as
                            drilled” survey performed after the cross-unit well is drilled and completed.{' '}
                        </Text>
                    </View>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        7. If Lessor owns less than the full mineral estate in all or any part of the leased premises, the royalties and shut-in
                        royalties payable hereunder for any well on any part of the leased premises or lands pooled therewith shall be reduced to the
                        proportion that Lessor’s interest in such part of the leased premises bears to the full mineral estate in such part of the
                        leased premises.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        8. The interest of either Lessor or Lessee hereunder may be assigned, devised or otherwise transferred in whole or in part, by
                        area and/or by depth or zone, and the rights and obligations of the parties hereunder shall extend to their respective heirs,
                        devisees, executors, administrators, successors and assigns. No change in Lessor's ownership shall have the effect of reducing
                        the rights or enlarging the obligations of Lessee hereunder, and no change in ownership shall be binding on Lessee until 60
                        days after Lessee has been furnished the original or certified or duly authenticated copies of the documents establishing such
                        change of ownership to the satisfaction of Lessee or until Lessor has satisfied the notification requirements contained in
                        Lessee's usual form of division order. In the event of the death of any person entitled to shut in royalties hereunder, Lessee
                        may pay or tender such shut in royalties to the credit of decedent or decedent's estate in the depository designated above. If
                        at any time two or more persons are entitled to shut in royalties hereunder, Lessee may pay or tender such shut in royalties
                        to such persons or to their credit in the depository, either jointly or separately in proportion to the interest which each
                        owns. If Lessee transfers its interest hereunder in whole or in part Lessee shall be relieved of all obligations thereafter
                        arising with respect to the transferred interest, and failure of the transferee to satisfy such obligations with respect to
                        the transferred interest shall not affect the rights of Lessee with respect to any interest not so transferred. If Lessee
                        transfers a full or undivided interest in all or any portion of the area covered by this lease, the obligation to pay or
                        tender shut in royalties hereunder shall be divided between Lessee and the transferee in proportion to the net acreage
                        interest in this lease then held by each.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        9. Lessee may, at any time and from time to time, deliver to Lessor or file of record a written release of this lease as to a
                        full or undivided interest in all or any portion of the area covered by this lease or any depths or zones thereunder, and
                        shall thereupon be relieved of all obligations thereafter arising with respect to the interest so released. If Lessee releases
                        all or an undivided interest in less than all of the area covered hereby, Lessee's obligation to pay or tender shut in
                        royalties shall be proportionately reduced in accordance with the net acreage interest retained hereunder.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        10. In exploring for, developing, producing and marketing oil, gas and other substances covered hereby on the leased premises
                        or lands pooled or unitized therewith, in primary and/or enhanced recovery, Lessee shall have the right of ingress and egress
                        along with the right to conduct such operations on the leased premises as may be reasonably necessary for such purposes,
                        including but not limited to geophysical operations, the drilling of wells, and the construction and use of roads, canals,
                        pipelines, tanks, water wells, disposal wells, injection wells, pits, electric and telephone lines, power stations, and other
                        facilities deemed necessary by Lessee to discover, produce, store, treat and/or transport production. Lessee may use in such
                        operations, free of cost, any oil, gas, water and/or other substances produced on the leased premises, except water from
                        Lessor's wells or ponds. In exploring, developing, producing or marketing from the leased premises or lands pooled therewith,
                        the ancillary rights granted herein shall apply (a) to the entire leased premises described in Paragraph 1 above,
                        notwithstanding any partial release or other partial termination of this lease; and (b) to any other lands in which Lessor now
                        or hereafter has authority to grant such rights in the vicinity of the leased premises or lands pooled therewith. When
                        requested by Lessor in writing, Lessee shall bury its pipelines below ordinary plow depth on cultivated lands. No well shall
                        be located less than 200 feet from any house or barn now on the leased premises or other lands used by Lessee hereunder,
                        without Lessors consent, and Lessee shall pay for damage caused by its operations to buildings and other improvements now on
                        the leased premises or such other lands, and to commercial timber and growing crops thereon. Lessee shall have the right at
                        any time to remove its fixtures, equipment and materials, including well casing, from the leased premises or such other lands
                        during the term of this lease or within a reasonable time thereafter.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        11. Lessee's obligations under this lease, whether express or implied, shall be subject to all applicable laws, rules,
                        regulations and orders of any governmental authority having jurisdiction including restrictions on the drilling and production
                        of wells, and the price of oil, gas and other substances covered hereby. When drilling, reworking, production or other
                        operations are prevented or delayed by such laws, rules, regulations or orders, or by inability to obtain necessary permits,
                        equipment, services, material, water, electricity, fuel, access or easements, or by fire, flood, adverse weather conditions,
                        war, sabotage, rebellion, insurrection, riot, strike or labor disputes, or by inability to obtain a satisfactory market for
                        production or failure of purchasers or carriers to take or transport such production, or by any other cause not reasonably
                        within Lessee's control, this lease shall not terminate because of such prevention or delay, and at Lessees option, the period
                        of such prevention or delay shall be added to the term hereof. Lessee shall not be liable for breach of any express or implied
                        covenants of this lease when drilling, production or other operations are so prevented, delayed or interrupted.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        12. In the event that Lessor, during the primary term of this lease, receives a bona fide offer which Lessor is willing to
                        accept from any party offering to purchase from Lessor a lease covering any or all of the substances covered by this lease and
                        covering all or a portion of the land described herein, with the lease becoming effective upon expiration of this lease,
                        Lessor hereby agrees to notify Lessee in writing of said offer immediately, including in the notice the name and address of
                        the offeror, the price offered and all other pertinent terms and conditions of the offer. Lessee, for a period of fifteen days
                        after receipt of the notice, shall have the prior and preferred right and option to purchase the lease or part thereof or
                        interest therein, covered by the offer at the price and according to the terms and conditions specified in the offer.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        13. No litigation shall be initiated by Lessor with respect to any breach or default by Lessee hereunder, for a period of at
                        least 90 days after Lessor has given Lessee written notice fully describing the breach or default, and then only if Lessee
                        fails to remedy the breach or default, within such period. In the event the matter is litigated and there is a final judicial
                        determination that a breach or default has occurred, this lease shall not be forfeited or cancelled in whole or in part unless
                        Lessee is given a reasonable time after said judicial determination to remedy the breach or default and Lessee fails to do so.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        14. Lessor hereby warrants and agrees to defend title conveyed to Lessee hereunder, and agrees that Lessee at Lessee's option
                        may pay and discharge any taxes, mortgages or liens existing, levied or assessed on or against the leased premises. If Lessee
                        exercises such option, Lessee shall be subrogated to the rights of the party to whom payment is made and, in addition to its
                        other rights, may reimburse itself out of any royalties or shut-in royalties otherwise payable to Lessor hereunder. In the
                        event Lessee is made aware of any claim inconsistent with Lessor's title, Lessee may suspend the payment of royalties and
                        shut-in royalties hereunder, without interest, until Lessee has been furnished satisfactory evidence that such claim has been
                        resolved.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        15. Should the right or interest of Lessee hereunder be disputed by Lessor, or any other person, the time covered by the
                        pendency of such dispute shall not be counted against Lessee either as affecting the term of the lease or for any other
                        purpose, and Lessee may suspend all payments without interest until there is a final adjudication or other determination of
                        such dispute.
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={false}>
                        16. Lessee is specifically authorized to utilize and traverse the sub-surface of the leased premises for all purposes
                        incidental to the exploration for oil and gas by means of directionally drilling a well or wells to be bottomed beneath other
                        lands not owned by Lessor, including the right to directionally drill, complete, operate and produce such well or wells, to
                        conduct any operations which may be necessary, useful or desirable in exploring for or producing minerals therefrom, to plug
                        or abandon such well or wells and to remove casing and other equipment therefrom, which rights, if commenced during the term
                        of this lease, shall continue until one hundred eighty (180) days after cessation of the use or exercise thereof.{' '}
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt4, styles.text, styles.bold, { textAlign: 'center', marginBottom: '4rem' }]}>
                        THE REST OF THIS PAGE IS INTENTIONALLY BLANK
                    </Text>
                    <Text style={[styles.fontNormal, styles.mt1, styles.text, styles.bold, { textAlign: 'center' }]}>
                        [ACKNOWLEDGMENTS AND SIGNATURES ARE DISPLAYED IN THE NEXT PAGES]
                    </Text>

                    {/* <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                        17. For the same consideration recited in this lease, Lessor hereby grants Lessee the option to extend the primary term of
                        this lease for an additional{' '}
                        {PropertyUtil.SafeGet(() => MathUtil.numberToEnglish(parseInt(leaseValues?.leasehold?.optionTerm, 10)), 'OPTION TERM WORD')} (
                        {PropertyUtil.SafeGet(() => leaseValues?.leasehold?.optionTerm, 'OPTION TERM')}) {leaseValues?.leasehold?.optionTermUnits}{' '}
                        from the expiration of the original primary term hereof as to all or any portion of the acreage then held hereunder which
                        would expire unless so extended. This option may be exercised by Lessee, or its successors and assigns, at any time during the
                        primary term hereof by tendering to Lessor the sum of{' '}
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(leaseValues?.leasehold?.optionBonusAmount, 10)),
                            'OPTION AMOUNT'
                        )}{' '}
                        (${parseInt(leaseValues?.leasehold?.optionBonusAmount, 10)}) per net mineral acre for each acre so extended, which payment
                        shall cover the{' '}
                        {PropertyUtil.SafeGet(() => MathUtil.numberToEnglish(parseInt(leaseValues?.leasehold?.optionTerm, 10)), 'OPTION TERM WORD')} (
                        {PropertyUtil.SafeGet(() => leaseValues?.leasehold?.optionTerm, 'OPTION TERM')}) {leaseValues?.leasehold?.optionTermUnits} of
                        the extended term and Lessor acknowledges that there will be no rental payments due for or during the extended term. Should
                        the Primary Term be extended as herein provided, it shall be considered that this lease originally provided for a Primary Term
                        of{' '}
                        {PropertyUtil.SafeGet(() => MathUtil.numberToEnglish(parseInt(leaseValues?.leasehold?.primaryTerm, 10)), 'OPTION TERM WORD')}{' '}
                        ({PropertyUtil.SafeGet(() => leaseValues.leasehold?.primaryTerm, 'OPTION TERM')}) {leaseValues?.leasehold?.primaryTermUnits}.
                        Should this option be exercised as herein provided, it is agreed that Lessee may execute and file of record an appropriate
                        recordable instrument evidencing the exercise of this option.
                    </Text> */}

                    <Text
                        style={styles.pageNumbers}
                        render={({ pageNumber }) => {
                            return `${pageNumber}`;
                        }}
                        fixed={true}
                    />
                </Page>

                {/* <Page size="LEGAL" style={styles.page}>

                    <Text style={[styles.fontNormal, styles.mt2, styles.text]}>
                        IN TESTIMONY WHEREOF, I sign the ____ day of ___________________, 20____,
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt4, styles.text]}>
                        LESSOR:
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt4, styles.text]}>
                        ______________________________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt1, styles.text]}>
                        {PropertyUtil.SafeGet(() => leaseValues.lessor.name, 'LESSOR')}
                    </Text>

                    <Text style={[styles.fontNormal, styles.center, styles.mt3, styles.h2]}>
                        (ACKNOWLEDGMENT)
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        STATE OF ________________________
                         </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        COUNTY OF ________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        Before me, the udnersigned, a Notary Public, in and for said county and State, on this ____ day of ____________________, 20___, personally appeared ____________________, an
                        individual, personally known to me
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt1]}>
                        to be the identical person who executed the within and foregoinginstrument, and acknowledged to me that he executed the same as his free and voluntary act and deed, for the
                        use and purposes therein set forth.
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt1]}>
                        Notary Public
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        Print Name _______________________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        My Commission expires: ________________________
                        </Text>

                    <Text style={[styles.fontNormal, styles.mt3]}>
                        Commission Number: ________________________
                        </Text>

                    <Text
                        style={styles.pageNumbers}
                        render={({ pageNumber }) => (
                            `${pageNumber}`
                        )}
                        fixed={true}
                    />

                    {this.signatureBlocks()}
                </Page> */}

                {PropertyUtil.SafeGet(() => leaseValues.acknowledgements.length > 0) && (
                    <Page size="LEGAL" style={styles.page} wrap={true}>
                        <View wrap={true}>
                            <Text style={[styles.fontNormal, styles.mt3, styles.sbTitle, styles.bold, styles.text]}>
                                IN WITNESS WHEREOF,{' '}
                                <Text style={[styles.fontNormal]}>
                                    this lease is executed to be effective as of the date first written above, but upon execution shall be binding on
                                    the signatory and the signatory's heirs, devisees, executors, administrators, successors and assigns, whether or
                                    not this lease has been executed by all parties hereinabove named as Lessor.
                                </Text>
                            </Text>
                            <View
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                {this.displaySignatureBlocks()}
                            </View>
                        </View>
                        <View wrap={true} style={styles.mt2}>
                            <Text style={[styles.acknowledgementHeading, styles.mt2, styles.underlined, styles.bold]}>ACKNOWLEDGMENT</Text>
                            {this.displayAcknowledgements()}
                        </View>
                        <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    </Page>
                )}
                {/* {PropertyUtil.SafeGet(() => leaseValues.acknowledgements.length > 0) && (
                    <Page size="LEGAL" style={styles.page}>
                        <Text style={[styles.acknowledgementHeading, styles.mt3]}>(ACKNOWLEDGEMENT)</Text>
                        {this.displayAcknowledgements()}
                        <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    </Page>
                )} */}

                <Page size="LEGAL" style={styles.page}>
                    <Text style={[styles.exhibitHeading, styles.bold]}>Exhibit A</Text>
                    <Text style={[{ fontSize: '10pt' }, styles.mt2]}>
                        Attached to and made part of that certain Paid Up Oil and Gas Lease dated{' '}
                        <Text style={[styles.bold]}>{PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('Do'), 'DAY')}</Text> day
                        of{' '}
                        <Text style={[styles.bold]}>
                            {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('MMMM'), 'MONTH')},{' '}
                            {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('YYYY'), 'YEAR')}
                        </Text>
                        , by and between{' '}
                        <Text style={[styles.bold]}>{PropertyUtil.SafeGet(() => leaseValues.ownersOnDocument, 'LESSOR NAME')}</Text>, as Lessor,
                        and{' '}
                        <Text style={[styles.bold, { fontSize: '9pt' }]}>
                            {PropertyUtil.SafeGet(() => leaseValues.lesseeName, 'LESSEE NAME')}
                        </Text>
                        , as Lessee.
                    </Text>
                    {leaseValues?.legalDescription?.length > 1000 &&
                        <>
                            <Text style={[styles.mt2, { fontSize: '10pt' }, styles.bold]}>LEGAL DESCRIPTION:</Text>
                            <Text style={[styles.fontNormal, styles.mt1, styles.text, { fontSize: '10pt' }]}>
                                {PropertyUtil.SafeGet(() => leaseValues.legalDescription, 'LEGAL DESCRIPTION')}
                            </Text>

                        </>
                    }

                    <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    {(PropertyUtil.SafeGet(() => leaseValues.adHocClauses.length > 0) ||
                        PropertyUtil.SafeGet(() => leaseValues.contractClauses.length > 0) ||
                        this.props?.contractData?.leasehold?.renewalOption === true) && (
                            <View wrap={true}>
                                <Text style={[styles.mt2, { fontSize: '10pt' }, styles.bold]}>ADDITIONAL TERMS AND PROVISIONS:</Text>
                                <Text style={[{ fontSize: '10pt' }, styles.mt1]}>
                                    Notwithstanding anything contained in this lease to the contrary, the following provisions shall control and prevail over
                                    any other:{' '}
                                </Text>

                                {this.displayOptionToExtend()}
                                {this.additionalProvisions()}
                                {this.adhocProvisions()}

                                <Text style={[{ fontSize: '10pt' }, styles.mt4, styles.text, styles.bold]}>
                                    ____________________________________
                                </Text>
                                <Text style={[{ fontSize: '10pt' }, styles.mt1, styles.text, styles.bold]}>{leaseValues?.lessor?.name || 'Lessor'}</Text>

                                {/* <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} /> */}
                            </View>
                        )}
                </Page>
            </Document>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/OGTemplates/AethonTemplate.tsx